import {
  useCallback,
  useState,
  useEffect,
  useRef,
  KeyboardEventHandler,
  useMemo,
  memo,
  ReactNode,
} from 'react';

import { twMerge } from 'tailwind-merge';

import { Text } from '../Text/Text';

import { defaultDropdownStyles, DropdownStyles } from './constants';

interface Option<T> {
  id: string;
  label: string;
  value: T;
  theme?: 'primary' | 'secondary';
}

interface DropdownProps<T> {
  containerClass?: string;
  customLabelComponent?: ReactNode;
  defaultOption: Option<T>;
  dropdownIcon?: ReactNode;
  dropdownLabel?: string;
  dropdownStyles?: DropdownStyles;
  onChange: (option: Option<T>) => void;
  options: Option<T>[];
}

const rapidSucessionKeyboardTimeout = 500;

function DropdownFunction<T>({
  containerClass,
  customLabelComponent,
  defaultOption,
  dropdownIcon,
  dropdownLabel,
  dropdownStyles,
  onChange,
  options,
}: DropdownProps<T>) {
  const mergedDropdownStyles = { ...defaultDropdownStyles, ...dropdownStyles };
  const [expanded, setExpanded] = useState<boolean>(false);
  const [activeOptionId, setActiveOptionId] = useState<(typeof options)[number]['id']>(
    defaultOption.id,
  );

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const keysSoFar = useRef<string>('');
  const rapidSucessionTimeout = useRef<null | NodeJS.Timeout>(null);

  const clearKeysSoFar = useCallback(() => {
    keysSoFar.current = '';
  }, []);
  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const keyboardEventToHandler = useMemo(
    () => ({
      Enter: () => {
        if (document.activeElement === buttonRef.current) {
          setExpanded(true);
        }
        if (document.activeElement === listboxRef.current) {
          setExpanded(false);
        }
      },
      Escape: () => {
        if (expanded) {
          setExpanded(false);
          buttonRef.current?.focus();
        }
      },
      ArrowDown: () => {
        if (document.activeElement === buttonRef.current) {
          setExpanded(true);
        }

        // Moves focus to and selects the next option.
        setActiveOptionId(
          previousId =>
            options[
              options.findIndex(({ id }) => id === previousId) >= options.length - 1
                ? options.length - 1
                : options.findIndex(({ id }) => id === previousId) + 1
            ]!.id,
        );
      },
      ArrowUp: () => {
        if (document.activeElement === buttonRef.current) {
          setExpanded(true);
        }

        // Moves focus to and selects the previous option.
        setActiveOptionId(
          previousId =>
            options[
              options.findIndex(({ id }) => id === previousId) <= 0
                ? 0
                : options.findIndex(({ id }) => id === previousId) - 1
            ]!.id,
        );
      },
      Home: () => {
        if (expanded) {
          setActiveOptionId(options[0]!.id);
        }
      },
      End: () => {
        if (expanded) {
          setActiveOptionId(options[options.length - 1]!.id);
        }
      },
    }),
    [expanded, options],
  );

  const selectActiveAsYouType = useCallback(
    ({ key }: any) => {
      if (rapidSucessionTimeout.current) {
        clearTimeout(rapidSucessionTimeout.current);
      }
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      keysSoFar.current += key;
      setActiveOptionId(previousId =>
        options.find(({ label }) => label.toLocaleLowerCase().startsWith(keysSoFar.current))
          ? options[
              options.findIndex(({ label }) =>
                label.toLocaleLowerCase().startsWith(keysSoFar.current),
              )
            ]!.id
          : previousId,
      );
      rapidSucessionTimeout.current = setTimeout(clearKeysSoFar, rapidSucessionKeyboardTimeout);
    },
    [clearKeysSoFar, options],
  );

  const handleKeyDown: KeyboardEventHandler<HTMLUListElement> = useCallback(
    (event): void => {
      event.preventDefault();
      if (event.key in keyboardEventToHandler) {
        // nosemgrep: javascript.lang.security.audit.unsafe-dynamic-method
        const handler = keyboardEventToHandler[event.key as keyof typeof keyboardEventToHandler];
        handler();
      }
      selectActiveAsYouType(event);
    },
    [keyboardEventToHandler, selectActiveAsYouType],
  );

  const handleBlur = useCallback(() => {
    setExpanded(false);
  }, []);

  const handleOptionClick = useCallback((id: string) => {
    setActiveOptionId(id);
    setExpanded(false);
  }, []);

  useEffect(() => {
    const handleMousedown = ({ target }: MouseEvent): void => {
      if (containerRef.current && target instanceof Node && containerRef.current.contains(target)) {
        return;
      }
      setExpanded(false);
    };
    const handleTouchstart = ({ target }: TouchEvent): void => {
      if (containerRef.current && target instanceof Node && containerRef.current.contains(target)) {
        return;
      }
      setExpanded(false);
    };

    document.addEventListener('mousedown', handleMousedown);
    document.addEventListener('touchstart', handleTouchstart);
    return () => {
      document.removeEventListener('mousedown', handleMousedown);
      document.removeEventListener('touchstart', handleTouchstart);
    };
  }, []);

  useEffect(() => {
    if (expanded) {
      listboxRef.current?.focus();
    }
  }, [expanded]);

  useEffect(() => {
    const relatedOption = options.find(({ id }) => id === activeOptionId)!;
    onChange(relatedOption);
  }, [options, activeOptionId, onChange]);

  return (
    <div className={twMerge('relative', containerClass)} ref={containerRef}>
      <header className={mergedDropdownStyles.headerClasses}>
        {customLabelComponent ?? (
          <span
            data-testid="variants-label"
            className={mergedDropdownStyles.labelClasses}
            id="listbox-label"
          >
            {dropdownLabel}
          </span>
        )}
        <button
          aria-expanded={expanded}
          aria-haspopup="listbox"
          aria-labelledby="listbox-label listbox-button"
          className={mergedDropdownStyles.buttonClasses}
          data-testid={`dropdown-button-${activeOptionId}`}
          id="listbox-button"
          onClick={toggleExpanded}
          type="button"
        >
          <Text
            variant="sm"
            constant
            className={twMerge(mergedDropdownStyles.previewOptionClasses, 'truncate')}
          >
            {options.find(({ id }) => id === activeOptionId)?.label}
          </Text>
          {dropdownIcon}
        </button>
      </header>
      <ul
        ref={listboxRef}
        id="listbox-list"
        tabIndex={0}
        role="listbox"
        aria-labelledby="listbox-label"
        aria-expanded={expanded}
        aria-activedescendant={activeOptionId}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={
          expanded
            ? mergedDropdownStyles.openedListboxClasses
            : mergedDropdownStyles.closedListboxClasses
        }
        data-testid="variants-list"
      >
        {options.map(({ label, id, theme }) => {
          const selected = id === activeOptionId;
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={id}
              id={id}
              className={twMerge(
                selected
                  ? mergedDropdownStyles.selectedOptionClasses
                  : mergedDropdownStyles.unselectedOptionClasses,
                theme && theme === 'secondary'
                  ? mergedDropdownStyles.secondaryOptionClasses
                  : mergedDropdownStyles.primaryOptionClasses,
                'truncate',
              )}
              role="option"
              aria-selected={selected}
              onClick={() => {
                handleOptionClick(id);
              }}
            >
              {label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export const Dropdown = memo(DropdownFunction) as typeof DropdownFunction;
